import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SEO_HEAD_APPLIED_EVENT } from "src/Utilities/seoSignal";

/**
 * Pushes virtual page views to GTM dataLayer on SPA route changes.
 * GTM can trigger GA4 (or other tags) from this event.
 *
 * The `page_view` push must report the destination page's FINAL title. On routes
 * whose `PageHead` only renders after an async fetch (tour detail, blog post, CMS
 * page), the title isn't known at navigation time — firing on route change alone
 * would report the PREVIOUS page's title. Instead we wait for the head-applied
 * signal that `PageHead` emits once it has written the title/metadata, then fire
 * (debounced so the site-wide + page tags applied in the same tick coalesce into
 * one push). A fallback timer covers routes that render no `PageHead` at all, so
 * a page_view is never dropped.
 */
const SETTLE_MS = 50;
const FALLBACK_MS = 1500;

export const GTMPageView = () => {
    const location = useLocation();

    useEffect(() => {
        if (typeof window === "undefined") return;

        let fired = false;
        let settleTimer: ReturnType<typeof setTimeout> | undefined;
        let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

        const push = () => {
            if (fired) return;
            fired = true;
            if (settleTimer) clearTimeout(settleTimer);
            if (fallbackTimer) clearTimeout(fallbackTimer);
            window.removeEventListener(SEO_HEAD_APPLIED_EVENT, onApplied);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "page_view",
                page_path: location.pathname + location.search,
                page_title: document.title,
                page_location: window.location.href,
            });
        };

        const onApplied = () => {
            if (fired) return;
            // A PageHead exists for this route; don't force-fire on the fallback.
            if (fallbackTimer) {
                clearTimeout(fallbackTimer);
                fallbackTimer = undefined;
            }
            // Debounce so multiple applies in the same commit settle to one push
            // carrying the latest (final) title.
            if (settleTimer) clearTimeout(settleTimer);
            settleTimer = setTimeout(push, SETTLE_MS);
        };

        window.addEventListener(SEO_HEAD_APPLIED_EVENT, onApplied);
        fallbackTimer = setTimeout(push, FALLBACK_MS);

        return () => {
            if (settleTimer) clearTimeout(settleTimer);
            if (fallbackTimer) clearTimeout(fallbackTimer);
            window.removeEventListener(SEO_HEAD_APPLIED_EVENT, onApplied);
        };
    }, [location]);

    return null;
};
